import { Drawer } from 'antd';
import { useZonesMapView } from '../../map/hooks/use-zones-map-view';
import { ZonePanel } from '../../editor/components/ZonePanel';
import { ZonesMapArea } from '../../map/components/ZonesMapArea';

interface ZoneSheetProps {
  /** A zone id, or `new`. */
  zoneId: string;
  /** After any saved change, so the table behind the sheet shows it. */
  onTableChanged: () => Promise<void>;
}

/**
 * A zone opened from the table: a wide sheet over it, the zone's fields beside a map of the zones the table
 * shows, so its shape is drawn with its neighbours in view without leaving the table. The same panel, place
 * search and drawing as the map view's; stacked, map first, on a phone.
 */
export function ZoneSheet({ zoneId, onTableChanged }: ZoneSheetProps) {
  const view = useZonesMapView(zoneId, 'list');
  const { closeZone, openZone } = view.navigation;

  async function onChanged() {
    await Promise.all([view.mapZones.reload(), onTableChanged()]);
  }

  return (
    <Drawer open placement="right" size="min(1200px, 100vw)" closable={false} title={null} onClose={closeZone} classNames={{ body: 'p-0!' }} aria-label="Zone">
      <div className="flex min-h-full flex-col lg:grid lg:h-full lg:grid-cols-[24rem_minmax(0,1fr)]">
        <div className="order-2 p-5 lg:order-1 lg:overflow-y-auto lg:border-r lg:border-line">
          <ZonePanel
            key={zoneId}
            bare
            zoneId={zoneId}
            map={view.map}
            zoneKinds={view.zoneKindsState.status === 'loaded' ? view.kinds : null}
            onChanged={onChanged}
            onClose={closeZone}
            onOpenZone={openZone}
          />
        </div>
        <section aria-label="Zone map" className="relative order-1 h-[45dvh] min-h-[18rem] p-3 lg:order-2 lg:h-full">
          <div className="relative h-full">
            <ZonesMapArea tileKey={view.tileKey} containerRef={view.containerRef} state={view.mapZones.state} />
          </div>
        </section>
      </div>
    </Drawer>
  );
}
